import React, {Component} from 'react';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {push} from 'connected-react-router/immutable';
import {Redirect, Prompt} from 'react-router-dom';
import {Portal} from 'react-portal';
import loadjs from 'loadjs';
import {Trans} from '@lingui/macro';

import {loadTests, stopTestingItem} from '../../actions/tests';
import {authExpiredError} from '../../actions/login';
import {loadSamples} from '../../actions/samples';
import {markTestAsComplete} from '../../actions/testroom';
import {getUserCampaign} from '../../reducers/userCampaign';
import {getCurrentTest} from '../../reducers/tests';
import {getSamples} from '../../reducers/samples';
import {getUserId, getUserData, isAssessor} from '../../reducers/user';

import {initTrackingLib} from '../../services/skillup';
import {HOME, LOGIN} from '../../services/routes';
import {getTestExitMessage} from '../../services/alerts';
import {withQueues} from '../../services/context';
import {toJS, compressData} from '../../services/utilities';

import Loader from '../../components/Loader';
import loaderStyles from '!!css-loader!../../components/Loader/Loader.css';

import cl from 'classnames';
import config from 'config';
import s from './TestContainer.css';

function loadOutcomesScript(src) {
  return new Promise((resolve, reject) => {
    try {
      loadjs(src, {
        success() {
          const alg = window.UNSAFE_computeOutcomes;
          if (!alg) {
            throw new Error('Algorithm not found');
          }

          resolve(alg);
        },
        error: reject,
      });
    } catch (e) {
      reject(e);
    }
  });
}

function isValidEntity(e) {
  return e && e.size > 0 && Number.isInteger(e.get('id'));
}

function shareTestData(test) {
  let data = {};
  if (test && test.size > 0) {
    data = toJS(test.get('jsonData'));
  }

  window.skillupData = compressData(data);
  window.computeOutcomesPromise = loadOutcomesScript(
    `/api/tests/${test.get('abbreviation')}/algorithm.js`
  );
}

class TestContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: false,
      initialized: false,
      blocked: false,
      samplesLoaded: !props.isAssessor,
      loader: {
        node: null,
        state: false,
        text: true,
      },
    };

    this.timeoutID = null;
  }

  componentDidMount() {
    this.injectLoader();
    const {isAssessor, loadTests, loadSamples} = this.props;

    if (isAssessor) {
      loadSamples().then(() => {
        this.initLib(this.props);
        this.setState({samplesLoaded: true});
      });
    }

    loadTests();
  }

  componentWillUnmount() {
    this.props.stopTestingItem();
    this.timeoutID && window.clearTimeout(this.timeoutID);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.hasValidData(this.props) && !this.state.initialized) {
      shareTestData(this.props.currentTest);
      this.setState({initialized: true}, () => this.initLib(this.props));
    }
  }

  hasValidData = props => {
    return (
      isValidEntity(props.userCampaign) &&
      isValidEntity(props.currentTest) &&
      Number.isInteger(props.userId)
    );
  };

  onUnauth = () => {
    authExpiredError();
    push(LOGIN);
  };

  initLibIfNeeded = props => {
    const {isAssessor, assessedUsers} = props;
    const {initialized} = this.state;
    if (!initialized && (!isAssessor || assessedUsers)) {
      this.initLib(props);
      this.setState({initialized: true});
    }
  };

  initLib = props => {
    const userData = {
      username: props.userData.get('username'),
      firstName: props.userData.get('firstName'),
      lastName: props.userData.get('lastName'),
      role: toJS(props.userData.get('role'), {}),
      gender: props.userData.get('gender'),
    };

    initTrackingLib({
      lang: props.userCampaign.getIn(['user', 'lang']),
      campaignId: props.userCampaign.get('id'),
      testId: props.currentTest.get('id'),
      userId: props.userId,
      userData,
      assessedUsers: toJS(props.assessedUsers, []),
      requestsQueue: props.requestsQueue,
      logsQueue: props.logsQueue,
      onClose: this.goToHome,
      onComplete: this.onComplete,
      onUnauth: this.onUnauth,
    });
  };

  injectLoader = () => {
    const that = this;

    const updateLoader = ({state, text = null}) => {
      if (!that.state.loader || !that.state.loader.node) {
        console.warn('No loader node configured');
        return;
      }

      that.setState(({loader}) => {
        const newState = {...loader, state};
        if (text !== null) {
          newState.text = text;
        }

        return {loader: newState};
      });
    };

    window.skillupLoader = {
      registerNode(contextDocument) {
        if (!contextDocument) {
          throw new Error('No context configured');
        }

        // Inject the loader styles in the context
        const newStyleEl = contextDocument.createElement('style');
        newStyleEl.innerText = loaderStyles.toString();
        contextDocument.head.appendChild(newStyleEl);

        // Inject the loader container
        const loaderContainer = contextDocument.createElement('div');
        loaderContainer.setAttribute('id', 'Loader');
        contextDocument.body.appendChild(loaderContainer);

        that.setState(({loader}) => ({
          loader: {...loader, node: loaderContainer},
        }));
      },
      start(text) {
        updateLoader({state: true, text});
      },
      stop() {
        updateLoader({state: false, text: ''});
      },
    };
  };

  goToHome = () => {
    this.setState({blocked: false}, () => this.props.push(HOME));
  };

  onComplete = ({testId}) => {
    this.setState({blocked: false});
    this.props.markTestAsComplete(testId);
  };

  getCurrentTestUrl = () => {
    const {currentTest} = this.props;

    return config.testBaseUrl + (currentTest ? currentTest.get('url') : '');
  };

  getFrameRef = e => (this.frame = e);

  reloadIFrame = () => {
    if (this.frame) {
      const url = this.getCurrentTestUrl();
      this.frame.contentWindow.location.replace(url);
    }
  };

  onLoad = () => {
    this.setState({blocked: true}, () => {
      this.timeoutID = setTimeout(() => {
        // Run the code in a setTimeout to allow preloadjs to load resources
        const title = this.frame.contentDocument.title;
        if (!title || title === '403 Forbidden') {
          this.setState({error: true});
        }
      }, 6000);
    });
  };

  render() {
    const {currentTest, className} = this.props;
    const {error, blocked, loader, initialized, samplesLoaded} = this.state;
    const url = this.getCurrentTestUrl();
    const color = currentTest && currentTest.getIn(['jsonData', 'color']);
    const hasTimer = Boolean(currentTest && currentTest.getIn(['jsonData', 'hasTimer']));
    const customExitMessage = currentTest && currentTest.getIn(['jsonData', 'exitMessage']);

    if (!currentTest || currentTest.size <= 0) {
      return <Redirect to={HOME} />;
    }

    return (
      <div className={cl(s.Page, className)}>
        <Prompt when={blocked} message={getTestExitMessage(hasTimer, customExitMessage)} />

        {loader.node && loader.state && (
          <Portal node={loader.node}>
            <Loader text={loader.text} color={color} />
          </Portal>
        )}

        {!error && initialized && samplesLoaded && (
          <div className={s.TestContainer}>
            <iframe ref={this.getFrameRef} src={url} className={s.Frame} onLoad={this.onLoad} />
          </div>
        )}

        {error && (
          <span className={s.Center}>
            <Trans id="test_container.label">Impossibile caricare l'attività</Trans>
          </span>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  const userCampaign = getUserCampaign(state);
  const currentTest = getCurrentTest(state);
  const userId = getUserId(state);
  const userData = getUserData(state);
  const assessedUsers = getSamples(state);

  return {
    currentTest,
    userCampaign,
    userId,
    userData,
    isAssessor: isAssessor(state),
    assessedUsers,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {push, loadTests, loadSamples, stopTestingItem, markTestAsComplete, authExpiredError},
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(withQueues(TestContainer));
